import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";

import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";

export default function CustomersTable({ onRowClick = () => {} }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();

  const tableKey = "customers";
  const storageKey = "v2_table_customers";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Kunden",
        route: "data/kundes",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          include: ["sachbearbeiter"],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: "display_name",
      header: "Kundenname",
      minSize: 300,
    },
    {
      accessorKey: "id_ov",
      header: "ID OV",
      size: 80,
    },
    {
      accessorKey: "sachbearbeiter",
      header: "Sachbearbeiter",
      Cell: ({ row }) => {
        const sb = row.original?.sachbearbeiter;
        if (!sb) {
          return null;
        }

        return sb.nachname && sb.vorname
          ? `${sb.vorname} ${sb.nachname}`
          : sb.email || sb.id_ov || "";
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      onRowClick={onRowClick}
    />
  );
}

CustomersTable.propTypes = {
  onRowClick: PropTypes.func,
};
